import React, { useState, useEffect } from 'react';
import { Upload, CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslate } from '../hooks/useTranslate';
import { useLanguage } from '../context/LanguageContext';
import { OptimizedImage } from '../components/ui/optimized-image';

const Admission = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const { currentLanguage } = useLanguage();

  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  // Traductions statiques
  const { translatedText: titleTranslated } = useTranslate("Admission");
  const { translatedText: subtitleTranslated } = useTranslate("Les Lignages de Bruxelles");
  const { translatedText: successTitleTranslated } = useTranslate("Demande soumise avec succès !");
  const { translatedText: successMessageTranslated } = useTranslate("Votre demande d'admission a bien été envoyée. La Commission des Preuves examinera votre dossier et vous contactera dans les meilleurs délais.");
  const { translatedText: backHomeTranslated } = useTranslate("Retour à l'accueil");
  const { translatedText: formTitleTranslated } = useTranslate("Formulaire d'admission");
  const { translatedText: formDescTranslated } = useTranslate("Remplissez le formulaire ci-dessous pour soumettre votre demande");
  const { translatedText: yourInfoTranslated } = useTranslate("Vos informations");
  const { translatedText: civilityTranslated } = useTranslate("Civilité");
  const { translatedText: selectTranslated } = useTranslate("Sélectionner");
  const { translatedText: lastNameTranslated } = useTranslate("Nom");
  const { translatedText: firstNameTranslated } = useTranslate("Prénom");
  const { translatedText: emailTranslated } = useTranslate("Email");
  const { translatedText: phoneTranslated } = useTranslate("Téléphone");
  const { translatedText: birthDateTranslated } = useTranslate("Date de naissance");
  const { translatedText: addressTranslated } = useTranslate("Adresse");
  const { translatedText: messageTranslated } = useTranslate("Message (optionnel)");
  const { translatedText: descendantsTranslated } = useTranslate("Descendants");
  const { translatedText: descendantOfTranslated } = useTranslate("Descendant de");
  const { translatedText: birthYearTranslated } = useTranslate("Année de naissance");
  const { translatedText: documentsTranslated } = useTranslate("Documents annexes");
  const { translatedText: clickUploadTranslated } = useTranslate("Cliquez pour téléverser des documents");
  const { translatedText: acceptedFormatsTranslated } = useTranslate("Formats acceptés : PDF, JPG, PNG, DOCX");
  const { translatedText: filesSelectedTranslated } = useTranslate("fichier(s) sélectionné(s)");
  const { translatedText: validateRequestTranslated } = useTranslate("Valider votre demande d'admission");
  const { translatedText: rgpdConsentTranslated } = useTranslate("J'accepte les conditions RGPD");
  const { translatedText: honourConsentTranslated } = useTranslate("J'affirme sur l'honneur que les données soumises sont sincères");
  const { translatedText: cotisationConsentTranslated } = useTranslate("J'accepte de payer la cotisation annuelle");
  const { translatedText: shareConsentTranslated } = useTranslate("J'accepte que mes informations soient partagées avec les autres membres");
  const { translatedText: submitButtonTranslated } = useTranslate("Soumettre le dossier");
  const { translatedText: sendingTranslated } = useTranslate("Envoi en cours...");
  const { translatedText: contentTranslated } = useTranslate(pageData?.content || "");
  const { translatedText: captchaLabelTranslated } = useTranslate("Sécurité (anti-spam)");
  const { translatedText: captchaErrorTranslated } = useTranslate("Veuillez résoudre le calcul de sécurité");
  const { translatedText: captchaWrongTranslated } = useTranslate("Réponse incorrecte. Veuillez réessayer.");

  // CAPTCHA simple
  const [captchaQuestion, setCaptchaQuestion] = useState({ num1: 0, num2: 0 });
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  
  // Générer une nouvelle question CAPTCHA
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptchaQuestion({ num1, num2 });
    setCaptchaAnswer('');
  };
  
  useEffect(() => {
    generateCaptcha(); // Générer CAPTCHA au chargement
  }, []);

  const [formData, setFormData] = useState({
    civilite: '',
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    date_naissance: '',
    adresse: '',
    message: '',
    descendant_de: '',
    annee_naissance_descendant: '',
    rgpd_consent: false,
    honneur_consent: false,
    cotisation_consent: false,
    partage_consent: false
  });

  const [files, setFiles] = useState([]);

  useEffect(() => {
    fetchPageData();
  }, []);

  const fetchPageData = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/site/pages/admission`);
      if (response.ok) {
        const data = await response.json();
        setPageData(data);
      }
    } catch (err) {
      console.error('Erreur chargement page:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    // Valider le CAPTCHA
    const expectedAnswer = captchaQuestion.num1 + captchaQuestion.num2;
    if (parseInt(captchaAnswer) !== expectedAnswer) {
      setError(captchaWrongTranslated);
      setSubmitting(false);
      generateCaptcha(); // Générer nouveau CAPTCHA
      return;
    }

    try {
      const formDataToSend = new FormData();
      
      // Ajouter tous les champs
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });

      // Ajouter les fichiers
      files.forEach(file => {
        formDataToSend.append('files', file);
      });

      const response = await fetch(`${API_BASE}/api/admission/submit`, {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitted(true);
        window.scrollTo(0, 0);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Erreur lors de la soumission');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderBottomColor: 'rgb(196, 150, 96)' }}></div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
        {pageData?.header_image && (
          <div 
            className="relative w-full overflow-hidden"
            style={{ 
              height: '600px',
              marginTop: '-80px',
              paddingTop: '80px'
            }}
          >
            <OptimizedImage 
              src={pageData.header_image.startsWith('/api/files/') || pageData.header_image.startsWith('http') ? (pageData.header_image.startsWith('http') ? pageData.header_image : `${API_BASE}${pageData.header_image}`) : `${API_BASE}/api/files/${pageData.header_image}`}
              alt="Admission"
              className="absolute inset-0 w-full h-full object-cover"
              priority={true}
              placeholder="blur"
            />
            <div className="absolute inset-0 bg-black bg-opacity-30"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ paddingTop: '100px' }}>
              <h1 
                className="text-5xl font-bold text-center px-4"
                style={{ 
                  fontFamily: '"Lora", serif',
                  color: 'rgb(196, 150, 96)',
                  textShadow: '3px 3px 6px rgba(0,0,0,0.8)'
                }}
              >
                {titleTranslated}
              </h1>
              <p className="text-2xl text-white text-center px-4 mt-3" style={{ fontFamily: '"Lora", serif', textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
                {subtitleTranslated}
              </p>
            </div>
          </div>
        )}

        <div className="max-w-3xl mx-auto px-4 py-16">
          <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Lora", serif' }}>
              {successTitleTranslated}
            </h2>
            <p className="text-gray-700 mb-4" style={{ fontFamily: '"Lora", serif' }}>
              {successMessageTranslated}
            </p>
            <button
              onClick={() => window.location.href = '/'}
              className="mt-4 px-6 py-2 text-white rounded-lg"
              style={{ backgroundColor: 'rgb(196, 150, 96)' }}
            >
              {backHomeTranslated}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header Image */}
      {pageData?.header_image && (
        <div 
          className="relative w-full overflow-hidden"
          style={{ 
            height: '600px',
            marginTop: '-80px',
            paddingTop: '80px'
          }}
        >
          <OptimizedImage 
            src={pageData.header_image.startsWith('/api/files/') || pageData.header_image.startsWith('http') ? (pageData.header_image.startsWith('http') ? pageData.header_image : `${API_BASE}${pageData.header_image}`) : `${API_BASE}/api/files/${pageData.header_image}`}
            alt="Admission"
            className="absolute inset-0 w-full h-full object-cover"
            priority={true}
            placeholder="blur"
          />
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ paddingTop: '100px' }}>
            <h1 
              className="text-5xl font-bold text-center px-4"
              style={{ 
                fontFamily: '"Lora", serif',
                color: 'rgb(196, 150, 96)',
                textShadow: '3px 3px 6px rgba(0,0,0,0.8)'
              }}
            >
              Admission
            </h1>
            <p className="text-2xl text-white text-center px-4 mt-3" style={{ fontFamily: '"Lora", serif', textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
              Les Lignages de Bruxelles
            </p>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Contenu de la page */}
        {pageData?.content && (
          <div dangerouslySetInnerHTML={{ __html: currentLanguage === 'fr' ? pageData.content : contentTranslated }}
            style={{ fontFamily: '"Lora", serif' }}
            className="mb-12"
          ></div>
        )}

        {/* Formulaire d'admission */}
        <div className="bg-white rounded-lg shadow-lg border p-8">
          <div className="mb-8 text-center">
            <div className="inline-block p-4 bg-amber-50 rounded-full mb-4">
              <Upload className="w-8 h-8" style={{ color: 'rgb(196, 150, 96)' }} />
            </div>
            <h2 className="text-3xl font-bold mb-2" style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Lora", serif' }}>
              {formTitleTranslated}
            </h2>
            <p className="text-gray-600" style={{ fontFamily: '"Lora", serif' }}>
              {formDescTranslated}
            </p>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <p className="text-red-800">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Vos informations */}
            <div>
              <h3 className="text-xl font-bold mb-4" style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Lora", serif' }}>
                {yourInfoTranslated}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">{civilityTranslated} *</label>
                  <select
                    required
                    value={formData.civilite}
                    onChange={(e) => setFormData({...formData, civilite: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">{selectTranslated}</option>
                    <option>Monsieur</option>
                    <option>Madame</option>
                    <option>Comte</option>
                    <option>Comtesse</option>
                    <option>Baron</option>
                    <option>Baronne</option>
                    <option>Vicomte</option>
                    <option>Vicomtesse</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{lastNameTranslated} *</label>
                  <input
                    type="text"
                    required
                    value={formData.nom}
                    onChange={(e) => setFormData({...formData, nom: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{firstNameTranslated} *</label>
                  <input
                    type="text"
                    required
                    value={formData.prenom}
                    onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{emailTranslated} *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{phoneTranslated} *</label>
                  <input
                    type="tel"
                    required
                    value={formData.telephone}
                    onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{birthDateTranslated} *</label>
                  <input
                    type="date"
                    required
                    value={formData.date_naissance}
                    onChange={(e) => setFormData({...formData, date_naissance: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">{addressTranslated} *</label>
                <textarea
                  required
                  value={formData.adresse}
                  onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="3"
                />
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">{messageTranslated}</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="4"
                />
              </div>
            </div>

            {/* Descendants */}
            <div>
              <h3 className="text-xl font-bold mb-4" style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Lora", serif' }}>
                {descendantsTranslated}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">{descendantOfTranslated}</label>
                  <input
                    type="text"
                    value={formData.descendant_de}
                    onChange={(e) => setFormData({...formData, descendant_de: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{birthYearTranslated}</label>
                  <input
                    type="text"
                    value={formData.annee_naissance_descendant}
                    onChange={(e) => setFormData({...formData, annee_naissance_descendant: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
            </div>

            {/* Documents */}
            <div>
              <h3 className="text-xl font-bold mb-4" style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Lora", serif' }}>
                {documentsTranslated}
              </h3>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.docx"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <div className="text-center">
                    <Upload className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm text-gray-600">
                      {clickUploadTranslated}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {acceptedFormatsTranslated}
                    </p>
                  </div>
                </label>
                {files.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium mb-2">{files.length} {filesSelectedTranslated}</p>
                    <ul className="text-sm text-gray-600">
                      {files.map((file, index) => (
                        <li key={index}>• {file.name}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Consentements */}
            <div>
              <h3 className="text-xl font-bold mb-4" style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Lora", serif' }}>
                {validateRequestTranslated}
              </h3>
              <div className="space-y-3">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    required
                    checked={formData.rgpd_consent}
                    onChange={(e) => setFormData({...formData, rgpd_consent: e.target.checked})}
                    className="mt-1"
                  />
                  <span className="text-sm">{rgpdConsentTranslated} *</span>
                </label>
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    required
                    checked={formData.honneur_consent}
                    onChange={(e) => setFormData({...formData, honneur_consent: e.target.checked})}
                    className="mt-1"
                  />
                  <span className="text-sm">{honourConsentTranslated} *</span>
                </label>
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    required
                    checked={formData.cotisation_consent}
                    onChange={(e) => setFormData({...formData, cotisation_consent: e.target.checked})}
                    className="mt-1"
                  />
                  <span className="text-sm">{cotisationConsentTranslated} *</span>
                </label>
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    required
                    checked={formData.partage_consent}
                    onChange={(e) => setFormData({...formData, partage_consent: e.target.checked})}
                    className="mt-1"
                  />
                  <span className="text-sm">{shareConsentTranslated} *</span>
                </label>
              </div>
            </div>

            {/* CAPTCHA simple */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <label className="text-lg font-semibold mb-2 block" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
                {captchaLabelTranslated}
              </label>
              <div className="flex items-center gap-3">
                <span className="text-xl font-bold" style={{ fontFamily: '"Lora", serif' }}>
                  {captchaQuestion.num1} + {captchaQuestion.num2} = ?
                </span>
                <input 
                  type="number"
                  placeholder="?"
                  className="w-20 text-lg p-2 text-center border border-gray-300 rounded"
                  style={{ fontFamily: '"Lora", serif' }}
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 text-white rounded-lg font-semibold text-lg"
              style={{ backgroundColor: 'rgb(196, 150, 96)' }}
            >
              {submitting ? sendingTranslated : submitButtonTranslated}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Admission;
